/**
 * Contact detail.
 */

import { useFetcher, useLoaderData } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { Contact, Organization } from '@erp/contracts'
import type { Assignment, Note, Task, TimelineResult } from '@erp/contracts'
import { api, ApiError } from '../api'
import { handleSubjectIntent } from '../detail-actions'
import { useUser } from '../status-context'
import { Detail, RecordDetail } from '../components/record-detail'
import { AssigneePicker } from '../components/assignee-picker'
import { Button, PageHeader } from '../ui'
import { contactName } from '../format'

interface Data {
  contact: Contact
  organization: Organization | null
  timeline: TimelineResult
  notes: Note[]
  tasks: Task[]
  assignments: Assignment[]
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Data> {
  const id = params.contactId
  if (!id) throw new Response('Missing contact id', { status: 400 })

  const [contact, timeline, notes, tasks, assignments] = await Promise.all([
    api.contacts.get(id),
    api.contacts.timeline(id, { limit: 50 }),
    api.notes.deletedFor('contact', id),
    api.tasks.forSubject('contact', id),
    api.assignments.forSubject('contact', id),
  ])

  // Sequential on purpose: the organization id is only known once the contact
  // has loaded, and most contacts have one so a conditional round trip is
  // cheaper than always fetching.
  const organization = contact.organizationId
    ? await api.organizations.get(contact.organizationId)
    : null

  return { contact, organization, timeline, notes: notes.rows, tasks: tasks.rows, assignments }
}

export async function action({ request, params }: ActionFunctionArgs) {
  const id = params.contactId
  if (!id) throw new Response('Missing contact id', { status: 400 })

  const form = await request.formData()
  const intent = String(form.get('intent'))

  const shared = await handleSubjectIntent(intent, form)
  if (shared) return shared

  try {
    if (intent === 'archive') {
      await api.contacts.archive(id)
      return { ok: true }
    }
    return { ok: false, message: 'Unknown action' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, issues: error.issues }
    throw error
  }
}

export default function ContactDetail() {
  const { contact, organization, timeline, notes, tasks, assignments } = useLoaderData() as Data
  const fetcher = useFetcher()
  const owner = useUser(contact.ownerId)

  return (
    <>
      <PageHeader
        title={contactName(contact)}
        actions={
          <>
            <a className="ax-btn" href={`/crm/contacts/${contact.id}/edit`}>
              Edit
            </a>
            {contact.archivedAt ? (
              <span className="ax-text-muted">Archived</span>
            ) : (
              <fetcher.Form method="post">
                <input type="hidden" name="intent" value="archive" />
                <Button type="submit" disabled={fetcher.state !== 'idle'}>
                  Archive
                </Button>
              </fetcher.Form>
            )}
          </>
        }
      />

      <RecordDetail
        subject={{ type: 'contact', id: contact.id }}
        timeline={timeline}
        notes={notes}
        tasks={tasks}
        side={
          <>
            <AssigneePicker subject={{ type: 'contact', id: contact.id }} assignments={assignments} />
            <Detail label="Owner" value={owner?.name ?? null} />
            <Detail label="Email" value={contact.email} />
            <Detail label="Phone" value={contact.phone} />
            <Detail label="Mobile" value={contact.mobile} />
            <Detail label="Job title" value={contact.jobTitle} />
            <div className="ax-field">
              <span className="ax-field-label">Organization</span>
              {organization ? (
                <a className="ax-value" href={`/crm/organizations/${organization.id}`}>
                  {organization.name}
                </a>
              ) : (
                <span className="ax-value">—</span>
              )}
            </div>
          </>
        }
      />
    </>
  )
}
